import type { SalesByProduct } from "@/@types/Analytics";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_AXIS_TICK,
  CHART_CURSOR,
  CHART_GRID_STROKE,
  PRIMARY_COLOR,
} from "../constants";
import { ChartCard } from "./ChartParts";

const AOV_COLOR = "#60A5FA";
const MAX_NAME_LENGTH = 15;

interface ProductSalesCardProps {
  salesByProduct?: SalesByProduct[];
  isLoading: boolean;
}

export function ProductSalesCard({ salesByProduct, isLoading }: ProductSalesCardProps) {
  const { t } = useTranslation("reports");
  const rows = useMemo(() => salesByProduct ?? [], [salesByProduct]);

  const chartData = useMemo(
    () =>
      rows.map((item) => ({
        name:
          item.product_name.length > MAX_NAME_LENGTH
            ? item.product_name.substring(0, MAX_NAME_LENGTH) + "..."
            : item.product_name,
        fullName: item.product_name,
        vendas: item.count,
        percentage: item.percentage,
        aov: item.average_order_value_cents / 100,
        aov_formatted: item.average_order_value_formatted,
        amount_formatted: item.amount_formatted,
      })),
    [rows],
  );

  return (
    <ChartCard
      title={t("reports:chart_sales_by_product")}
      description={t("reports:chart_sales_by_product_desc")}
      isLoading={isLoading}
      isEmpty={rows.length === 0}
      heightClassName="h-[350px]"
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="name" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="left"
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => value.toLocaleString("pt-BR")}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `R$ ${value.toLocaleString("pt-BR")}`}
              />
              <RechartsTooltip
                cursor={CHART_CURSOR}
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  return (
                    <div className="bg-zinc-900 border border-colorBorder px-3 py-2 rounded-lg shadow-xl">
                      <p className="text-sm font-medium text-colorText mb-2">
                        {payload[0]?.payload?.fullName || label}
                      </p>
                      {payload.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-muted-foreground">{entry.name}:</span>
                          <span className="text-colorText font-medium">
                            {entry.name === "AOV"
                              ? entry.payload?.aov_formatted
                              : entry.payload?.amount_formatted || entry.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="vendas"
                name={t("reports:sales_count")}
                fill={PRIMARY_COLOR}
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="aov"
                name="AOV"
                stroke={AOV_COLOR}
                strokeWidth={2}
                dot={{ fill: AOV_COLOR, strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="xl:col-span-1">
          <div className="bg-zinc-900/50 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-colorBorder">
              <h4 className="text-xs font-semibold text-colorText">{t("reports:product_details")}</h4>
            </div>
            <div className="max-h-[260px] overflow-y-auto">
              {rows.map((product, index) => (
                <div
                  key={product.product_id + index}
                  className="p-3 border-b border-colorBorder last:border-b-0 hover:bg-zinc-800/50 transition-colors"
                >
                  <p className="text-xs font-medium text-colorText mb-2 truncate" title={product.product_name}>
                    {product.product_name}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div>
                      <span className="text-muted-foreground">{t("reports:sales_label")}:</span>
                      <span className="text-colorText ml-1">{product.count}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">%:</span>
                      <span className="text-colorText ml-1">{product.percentage?.toFixed(1)}%</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">{t("reports:value_label")}:</span>
                      <span className="text-primary ml-1 font-medium">{product.amount_formatted}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}
